import { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { genderOptions, genderOptionsWithNeutral } from '@common/constants';
import { getGenderOptionsForRestriction } from '@common/helpers/genderRestriction';
import { useTranslations } from '@lang';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import { useExistingGuestSelect } from '@utils/useExistingGuestSelect';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import type { ExistingGuest, GuestConfig, OptionsPanelConfig } from 'voyager';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';
import { mapConfigGroupToOptionsPanel } from '../SpaDayBooking.helpers';

type UseGuestConfigParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  watchedGuests: SpaPackageFormValues['guests'] | undefined;
  configGroupsNoExtras: ConfigurationGroupType[];
  showGenderNeutral: boolean;
  genderSpecific: 'M' | 'F' | null;
  existingGuests: ExistingGuest[];
};

export function useGuestConfig({
  form,
  watchedGuests,
  configGroupsNoExtras,
  showGenderNeutral,
  genderSpecific,
  existingGuests,
}: UseGuestConfigParams) {
  const { t } = useTranslations();

  const titleOptions = useMemo(
    () => [...maleTitlesOptions, ...femaleTitlesOptions].map((o) => t(o.label)),
    [t]
  );

  const genderOpts = useMemo(() => {
    const opts = showGenderNeutral ? genderOptionsWithNeutral : genderOptions;
    const translated = opts.map((o) => ({ value: o.value, label: t(o.id) }));
    return getGenderOptionsForRestriction(genderSpecific, translated).genderOpts;
  }, [showGenderNeutral, genderSpecific, t]);

  const titleValueToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    [...maleTitlesOptions, ...femaleTitlesOptions].forEach((o) => {
      map[o.value] = t(o.label);
    });
    return map;
  }, [t]);

  const titleLabelToValue = useMemo(() => {
    const map: Record<string, string> = {};
    [...maleTitlesOptions, ...femaleTitlesOptions].forEach((o) => {
      map[t(o.label)] = o.value;
    });
    return map;
  }, [t]);

  const guests: GuestConfig[] = useMemo(() => {
    return (watchedGuests || []).map((guest, idx) => {
      const guestConfigGroups = guest.configurationGroups;
      const optionsPanels: OptionsPanelConfig[] = configGroupsNoExtras
        .filter((cg) => cg.isRequired)
        .map((cg) => mapConfigGroupToOptionsPanel(cg, guestConfigGroups))
        .filter((p): p is OptionsPanelConfig => p !== null);

      const guestFormValues = {
        title: titleValueToLabel[guest.title] || guest.title || '',
        firstName: guest.firstName || '',
        lastName: guest.lastName || '',
        gender: guest.gender || '',
      };

      return {
        guestNumber: idx + 1,
        values: guestFormValues,
        existingGuests,
        selectedGuestId: guest.selectedGuestId,
        optionsPanels,
      };
    });
  }, [watchedGuests, configGroupsNoExtras, titleValueToLabel, existingGuests]);

  const defaultGender = genderSpecific === 'M' ? 'male' : genderSpecific === 'F' ? 'female' : '';

  const handleGuestCountChange = useCallback(
    (count: number) => {
      const current = form.getValues('guests') || [];
      let nextGuests = current;
      if (count > current.length) {
        nextGuests = [
          ...current,
          ...Array.from({ length: count - current.length }, () => ({
            title: '',
            firstName: '',
            lastName: '',
            gender: defaultGender,
          })),
        ];
      } else if (count < current.length) {
        nextGuests = current.slice(0, count);
      }
      form.setValue('guests', nextGuests as SpaPackageFormValues['guests'], {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [form, defaultGender]
  );

  const handleGuestChange = useCallback(
    (guestIndex: number, field: string, value: string) => {
      const currentGuests = form.getValues('guests') || [];
      const updatedGuests = currentGuests.map((guest, idx) => {
        if (idx !== guestIndex) return guest;
        if (field === 'title') {
          const actualValue = titleLabelToValue[value] || value;
          return { ...guest, title: actualValue };
        }
        return { ...guest, [field]: value };
      });
      form.setValue('guests', updatedGuests as SpaPackageFormValues['guests'], {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [form, titleLabelToValue]
  );

  const spaDaySetValueOptions = useMemo(() => ({ shouldValidate: true, shouldDirty: true }), []);

  const handleExistingGuestSelect = useExistingGuestSelect({
    form,
    existingGuests,
    titleLabelToValue,
    defaultGender,
    setValueOptions: spaDaySetValueOptions,
  });

  return {
    titleOptions,
    genderOpts,
    guests,
    handleGuestCountChange,
    handleGuestChange,
    handleExistingGuestSelect,
  };
}
